import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import { API } from "../lib/api";
import { useOutlet } from "../context/OutletContext";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";

export function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  useEffect(() => { API.get("/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
      <Seo title="Shop by Category | Suvikka" description="Explore Suvikka categories — cosmetics, jewellery, bags, fashion and lingerie. Everything she loves, under one roof." canonicalPath="/categories" />
      <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Categories</p>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-900 mt-3">Everything She Loves. Under One Roof.</h1>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-10">
        {categories.map((c) => (
          <Link key={c.id} to={c.slug === "trending-now" ? "/trending" : `/category/${c.slug}`}
            data-testid={`categories-page-card-${c.slug}`} className="group relative overflow-hidden bg-stone-200 aspect-[4/5]">
            {c.image && <img src={c.image} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
            <div className="absolute inset-0 bg-gradient-to-t from-stone-950/85 via-stone-950/20 to-transparent" />
            <div className="absolute bottom-0 p-4 sm:p-6">
              <h3 className="font-display text-xl sm:text-2xl font-semibold text-white">{c.name}</h3>
              <p className="text-xs sm:text-sm text-stone-300 mt-1">{c.description}</p>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-white mt-3 border-b border-[#6A1B29] pb-0.5">EXPLORE <ArrowRight className="w-3 h-3" /></span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Products({ mode = "all" }) {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState(params.get("search") || "");
  const [category, setCategory] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [filter, setFilter] = useState(mode === "trending" ? "trending" : mode === "new" ? "new_arrival" : "");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const { outlet, setOutlet } = useOutlet();

  useEffect(() => { API.get("/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (slug && categories.length) {
      const c = categories.find((x) => x.slug === slug);
      setCategory(c ? c.name : "");
    } else if (!slug) setCategory("");
  }, [slug, categories]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page, limit: 12 });
      if (search) qs.set("search", search);
      if (category) qs.set("category", category);
      if (maxPrice) qs.set("max_price", maxPrice);
      if (filter === "trending") qs.set("trending", "true");
      if (filter === "new_arrival") qs.set("new_arrival", "true");
      if (outlet) qs.set("outlet", outlet.id);
      const { data } = await API.get(`/products?${qs}`);
      setData(data);
    } finally { setLoading(false); }
  }, [page, search, category, maxPrice, filter, outlet]);

  useEffect(() => { load(); }, [load]);

  const title = mode === "trending" ? "Trending Now" : category ? category : "Shop Suvikka";
  const seoTitle = mode === "trending" ? "Trending Now | Suvikka"
    : category ? `${category} | Shop ${category} Online — Suvikka`
    : "Shop All Products | Women's Fashion, Beauty & Jewellery — Suvikka";
  const seoDesc = mode === "trending"
    ? "Discover the most-loved trending products at Suvikka — bestselling cosmetics, jewellery, bags and fashion updated regularly."
    : category ? `Shop ${category} at Suvikka — trend-led designs at honest prices, available across India.`
    : "Browse the full Suvikka catalogue — cosmetics, jewellery, bags, fashion and accessories for the modern woman.";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="products-page">
      <Seo title={seoTitle} description={seoDesc} canonicalPath={mode === "trending" ? "/trending" : "/products"} noindex={!!search} />
      <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Catalogue</p>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-900 mt-3">{title}</h1>

      <div className="flex flex-col md:flex-row gap-3 mt-8" data-testid="product-filters">
        <form onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }} className="flex items-center border border-stone-300 bg-white rounded-full px-4 py-2.5 flex-1">
          <Search className="w-4 h-4 text-stone-400" />
          <input data-testid="products-search-input" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by product, category or SKU" className="ml-2 text-sm outline-none flex-1" />
        </form>
        <select data-testid="filter-category" value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }}
          className="border border-stone-300 bg-white rounded-full px-4 py-2.5 text-sm">
          <option value="">All Categories</option>
          {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <select data-testid="filter-tag" value={filter} onChange={(e) => { setFilter(e.target.value); setPage(1); }}
          className="border border-stone-300 bg-white rounded-full px-4 py-2.5 text-sm">
          <option value="">All Products</option>
          <option value="trending">Trending</option>
          <option value="new_arrival">New Arrival</option>
        </select>
        <select data-testid="filter-price" value={maxPrice} onChange={(e) => { setMaxPrice(e.target.value); setPage(1); }}
          className="border border-stone-300 bg-white rounded-full px-4 py-2.5 text-sm">
          <option value="">Any Price</option>
          <option value="500">Under ₹500</option>
          <option value="1000">Under ₹1,000</option>
          <option value="2000">Under ₹2,000</option>
          <option value="5000">Under ₹5,000</option>
        </select>
      </div>

      {outlet && (
        <div className="mt-4 inline-flex items-center gap-2 bg-[#FFF0F4] border border-[#6A1B29]/30 rounded-full px-4 py-2 text-sm" data-testid="outlet-active-chip">
          <span className="text-stone-700">Live stock at <strong>{outlet.name}</strong></span>
          <button onClick={() => setOutlet(null)} className="text-[#6A1B29] font-semibold" data-testid="outlet-clear-chip">Clear</button>
        </div>
      )}

      {loading ? (
        <p className="text-stone-500 mt-12" data-testid="products-loading">Loading products…</p>
      ) : data.items.length === 0 ? (
        <p className="text-stone-500 mt-12" data-testid="products-empty">No products found. Try a different search or filter.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 mt-8" data-testid="products-grid">
          {data.items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}

      {data.pages > 1 && (
        <div className="flex justify-center gap-2 mt-10" data-testid="products-pagination">
          {Array.from({ length: data.pages }, (_, i) => (
            <button key={i} data-testid={`page-${i + 1}`} onClick={() => setPage(i + 1)}
              className={`w-9 h-9 rounded-full text-sm font-medium ${page === i + 1 ? "bg-[#6A1B29] text-white" : "bg-white border border-stone-300 text-stone-700"}`}>
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
